import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { graphql } from '../../gql'
import { useGQLMutation } from '../../hooks/GQLUtilityHooks'
import { useCore, useLogin } from '../../hooks'
import { toastError, toastSuccess, toastWarning } from '../toastHelpers'

// GraphQL: create disposal
export const CreateDisposalMutation = graphql(`
  mutation CreateDisposal($company: Int, $carrier: String, $packages: [Int]) {
    createDisposal(company: $company, carrier: $carrier, packages: $packages) {
      data {
        id
      }
      errors
    }
  }
`)

// GraphQL: send disposal
export const SendDisposalMutation = graphql(`
  mutation SendDisposal($id: String!) {
    sendDisposal(id: $id) {
      data {
        id
      }
      errors
    }
  }
`)

interface BulkProgress {
  current: number
  total: number
  currentGroup: string
}

interface BulkResult {
  success: number
  failed: number
}

export const useDisposalCreate = () => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const { company } = useCore()
  const { user } = useLogin()

  const childCompanies = user.company.children || []

  // Filter state
  const [selectedCompany, setSelectedCompany] = useState<number | undefined>(undefined)
  const [selectedCarrier, setSelectedCarrier] = useState<string[]>([])
  const [selectedPackageIds, setSelectedPackageIds] = useState<number[]>([])

  // Bulk disposal state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false)
  const [bulkProgress, setBulkProgress] = useState<BulkProgress>({ current: 0, total: 0, currentGroup: '' })
  const [bulkResult, setBulkResult] = useState<BulkResult>({ success: 0, failed: 0 })
  const [isBulkCreating, setIsBulkCreating] = useState(false)

  // Set default company if no child companies
  useEffect(() => {
    if (childCompanies.length === 0 && company?.id) {
      setSelectedCompany(company.id)
    }
  }, [childCompanies, company?.id])

  // Reset selection when filters change
  useEffect(() => {
    setSelectedPackageIds([])
  }, [selectedCompany, selectedCarrier])

  // Mutations
  const createDisposalMutation = useGQLMutation(CreateDisposalMutation, ['createDisposal'])
  const sendDisposalMutation = useGQLMutation(SendDisposalMutation, ['sendDisposal'])

  // Selection handlers
  const toggleAll = (packages: any[]) => {
    if (!packages?.length) return
    if (selectedPackageIds.length === packages.length) {
      setSelectedPackageIds([])
    } else {
      setSelectedPackageIds(packages.map((p: any) => p.id))
    }
  }

  const toggleOne = (id: number) => {
    setSelectedPackageIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const toggleRange = (ids: number[]) => {
    setSelectedPackageIds(prev => {
      const allSelected = ids.every(id => prev.includes(id))
      if (allSelected) {
        // If all are selected, deselect them
        return prev.filter(id => !ids.includes(id))
      } else {
        // Otherwise, add all that aren't already selected
        const newIds = ids.filter(id => !prev.includes(id))
        return [...prev, ...newIds]
      }
    })
  }

  // Create single disposal
  const handleCreate = async () => {
    if (!selectedCompany || selectedCarrier.length !== 1) {
      toastWarning('Před vytvořením svozu musíte vybrat firmu a dopravce')
      return
    }
    if (selectedPackageIds.length === 0) return

    try {
      const res = await createDisposalMutation.mutateAsync({
        company: selectedCompany,
        carrier: selectedCarrier[0],
        packages: selectedPackageIds,
      } as any)

      const disposalId = res?.createDisposal?.data?.id
      if (!disposalId) {
        toastError('Svoz se nepodařilo vytvořit')
        return
      }

      try {
        await sendDisposalMutation.mutateAsync({ id: String(disposalId) })
        toastSuccess('Svoz vytvořen a odeslán')
      } catch {
        toastWarning('Svoz vytvořen, ale odeslání selhalo')
      }

      queryClient.invalidateQueries({ queryKey: ['packageDisposals'] })
      history.push('/disposal')
    } catch (e: any) {
      toastError(e?.message || 'Chyba při vytváření svozu')
    }
  }

  // Create bulk disposals
  const handleBulkCreate = async (packages: any[]) => {
    if (selectedPackageIds.length === 0) {
      toastWarning('Vyberte alespoň jeden balík')
      return
    }

    // Group packages by company and carrier
    const selectedPackages = packages.filter((p: any) => selectedPackageIds.includes(p.id))
    const invalidPackages = selectedPackages.filter((pkg: any) => !pkg.order?.company?.id || !pkg.order?.carrier?.id)

    if (selectedPackages.length !== selectedPackageIds.length || invalidPackages.length > 0) {
      toastWarning('Všechny vybrané balíky musí mít přiřazenou firmu a dopravce')
      return
    }

    const packagesByCompanyAndCarrier = selectedPackages.reduce<Record<string, any[]>>((acc, pkg: any) => {
      const companyId = pkg.order?.company?.id
      const carrierId = pkg.order?.carrier?.id
      if (!companyId || !carrierId) return acc

      const key = `${companyId}:${carrierId}`
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(pkg)
      return acc
    }, {})

    const groups = Object.values(packagesByCompanyAndCarrier)
    if (groups.length === 0) {
      toastWarning('Žádné balíky s firmou a dopravcem k vytvoření svozu')
      return
    }

    setIsBulkModalOpen(true)
    setIsBulkCreating(true)
    setBulkProgress({ current: 0, total: groups.length, currentGroup: '' })
    setBulkResult({ success: 0, failed: 0 })

    let successCount = 0
    let errorCount = 0

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i]
      const companyId = group[0]?.order?.company?.id
      const companyName = group[0]?.order?.company?.name || companyId
      const carrierId = group[0]?.order?.carrier?.id
      const carrierName = group[0]?.order?.carrier?.name || carrierId

      setBulkProgress({
        current: i + 1,
        total: groups.length,
        currentGroup: `${companyName} – ${carrierName}`,
      })

      try {
        const res = await createDisposalMutation.mutateAsync({
          company: companyId,
          carrier: carrierId,
          packages: group.map((p: any) => p.id),
        } as any)

        const disposalId = res?.createDisposal?.data?.id
        if (!disposalId) {
          errorCount++
        } else {
          try {
            await sendDisposalMutation.mutateAsync({ id: String(disposalId) })
            successCount++
          } catch {
            errorCount++
          }
        }
      } catch {
        errorCount++
      }
    }

    setIsBulkCreating(false)
    setBulkResult({ success: successCount, failed: errorCount })
    queryClient.invalidateQueries({ queryKey: ['packageDisposals'] })

    if (successCount > 0) {
      toastSuccess(`Vytvořeno a odesláno ${successCount} svozů`)
    }
    if (errorCount > 0) {
      toastError(`Selhalo ${errorCount} svozů`)
    }

    setTimeout(() => {
      setIsBulkModalOpen(false)
      history.push('/disposal')
    }, 2000)
  }

  // Reset filters
  const resetFilters = () => {
    setSelectedCarrier([])
    setSelectedCompany(childCompanies.length === 0 ? company?.id : undefined)
  }

  return {
    // State
    selectedCompany,
    setSelectedCompany,
    selectedCarrier,
    setSelectedCarrier,
    selectedPackageIds,
    childCompanies,
    company,

    // Mutations
    createDisposalMutation,
    sendDisposalMutation,

    // Bulk state
    isBulkModalOpen,
    bulkProgress,
    bulkResult,
    isBulkCreating,

    // Handlers
    toggleAll,
    toggleOne,
    toggleRange,
    handleCreate,
    handleBulkCreate,
    resetFilters,
  }
}
